import type { BacklogTaskResult } from '@ai-meeting/shared'
import { isAlreadyCreated, needsConfirmation } from './backlogConfirmDraft'

export interface BacklogSyncTally {
  created: number
  updated: number
  needsConfirm: number
  skipped: number
  /** Rows the server settled by finding the ticket an earlier press created. */
  duplicate: number
  failed: number
  /** 1-based row number of the first row still waiting, or null when none is. */
  firstPending: number | null
}

/**
 * Count what the last push did, per outcome.
 *
 * `skipped` is the reviewer's own unticking, not `skipped_duplicate` — nothing
 * is sent for an unticked row, so it has no verdict to count. The server's own
 * duplicates are counted separately as `duplicate`: they used to fall through
 * every branch here, which quietly left the bar's numbers short of the number
 * of rows on the table.
 *
 * @param rows Verdicts by action-item index, as the push left them.
 * @param skipped Indexes the reviewer unticked.
 * @param total How many action items the table is showing.
 */
export function tallySync(
  rows: Map<number, BacklogTaskResult>,
  skipped: Set<number>,
  total: number
): BacklogSyncTally {
  const tally: BacklogSyncTally = {
    created: 0,
    updated: 0,
    needsConfirm: 0,
    skipped: 0,
    duplicate: 0,
    failed: 0,
    firstPending: null,
  }
  for (let index = 0; index < total; index += 1) {
    const row = rows.get(index)
    // Ahead of the tick, not behind it: once the server has created the ticket
    // the reviewer's tick describes nothing, and counting such a row as
    // "skipped" would contradict the table, which reads it as created whether
    // the box was left ticked or not.
    if (isAlreadyCreated(row)) {
      tally.duplicate += 1
      continue
    }
    if (skipped.has(index)) {
      tally.skipped += 1
      continue
    }
    if (needsConfirmation(row)) {
      tally.needsConfirm += 1
      if (tally.firstPending === null) tally.firstPending = index + 1
      continue
    }
    if (row?.status === 'created') tally.created += 1
    else if (row?.status === 'updated') tally.updated += 1
    else if (row?.status === 'failed') tally.failed += 1
  }
  return tally
}
